import {
	ArrowBack,
	MoreVert,
	Delete,
	DeleteForever,
	SwitchAccount,
	ArrowForward,
	Search,
	Cancel, Edit
} from "@mui/icons-material"
import {
	Box,
	Container,
	IconButton,
	Menu,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Button,
	Select,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogActions, Link, Snackbar, Alert, Divider, DialogContent, TextField, useTheme, InputBase, TableSortLabel,
} from "@mui/material"
import React, {useEffect, useMemo, useState} from "react"
import moment                                from "moment"
import Grid from "@mui/material/Unstable_Grid2"
import { useNavigate }           from "react-router-dom"
import {doGet, doDelete, doPost} from "../../util/do-fetch"
import { logger }                from "../../util/log-utils"
import {storeAdminSession}                                         from "../../util/admin"
import getSupabase                                                 from "../../util/supabase-utils"
import LeftRight                                                   from "../LeftRight"

const log = logger("Users", 1)

function Users() {
	const navigate = useNavigate()
	const theme = useTheme()
	const [users, setUsers] = useState([])
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(50) // Default page size
	const [totalCount, setTotalCount] = useState(0)
	const [menuAnchor, setMenuAnchor] = useState(null)
	const [selectedUser, setSelectedUser] = useState(null)
	const [searchTerm, setSearchTerm] = useState('')
	const [searchedTerm, setSearchedTerm] = useState('')
	const [sortBy, setSortBy] = useState('created_at')
	const [sortDir, setSortDir] = useState('desc')
	const [error, setError] = useState(null)
	const [showForceDelete, setShowForceDelete] = useState(false)
	const [typeDelete, setTypeDelete] = useState('')
	const isDeleteConfirmed = useMemo(() => {
		return selectedUser &&
			typeDelete === selectedUser.email
	}, [selectedUser, typeDelete])

	useEffect(() => {
		fetchUsers()
	}, [page, pageSize])

	useEffect(() => {
		fetchUsers()
	}, [searchedTerm, sortDir, sortBy])

	const fetchUsers = async () => {
		try {
			const response = await doGet(`/admin/users?page=${page}&limit=${pageSize}&s=${searchTerm}&sortBy=${sortBy}&sortDir=${sortDir}`)
			const data = await response.json()
			setUsers(data.users)
			setTotalCount(data.totalCount)
		} catch (err) {
			log.error(err)
		}
	}

	const handleNextPage = () => {
		if (page * pageSize < totalCount) {
			setPage(page + 1)
		}
	}

	const handlePrevPage = () => {
		if (page > 1) {
			setPage(page - 1)
		}
	}

	const handlePageSizeChange = (event) => {
		setPageSize(event.target.value)
		setPage(1) // Reset to first page when page size changes
	}

	const handleCloseForceDelete = () => {
		setTypeDelete("")
		setShowForceDelete(false)
	}

	const handleMenuOpen = (event, user) => {
		setMenuAnchor(event.currentTarget)
		setSelectedUser(user)
	}

	const handleMenuClose = () => {
		setMenuAnchor(null)
		setSelectedUser(null)
	}

	const handleDeleteUser = async () => {
		try {
			const response = await doDelete(`/admin/users/${selectedUser.id}`)
			if(!response.ok) {
				throw response.statusText
			}
			setUsers(users.filter((user) => user.id !== selectedUser.id))
			handleMenuClose()
		} catch (err) {
			setError(`Failed to delete user: ${err?.message || err || "Unknown error"}`)
			log.error(err)
			handleMenuClose()
		}
	}

	const handleForceDeleteUser = async () => {
		try {
			const response = await doPost(`/admin/users/${selectedUser.user_id}/force-delete`)
			if(!response.ok) {
				throw response.statusText
			}
			setUsers(users.filter((user) => user.user_id !== selectedUser.user_id))
			handleCloseForceDelete()
			handleMenuClose()
		} catch (err) {
			setError(`Failed to delete user: ${err?.message || err || "Unknown error"}`)
			log.error(err)
			handleMenuClose()
		}
	}

	const handleImpersonateUser = async () => {
		try {
			const response = await doPost(`/admin/users/${selectedUser.user_id}/impersonate`)
			if(!response.ok) {
				throw response.statusText
			}
			const data = await response.json()
			const magiclink = data.magiclink

			if(magiclink) {
				// store admin session for later
				await storeAdminSession()
				// eslint-disable-next-line no-restricted-globals
				if(confirm("Impersonate user?")) {
					window.location.href = magiclink
				}
			}

			handleMenuClose()
		} catch (err) {
			setError(`Failed to impersonate user: ${err?.message || err || "Unknown error"}`)
			log.error(err)
			handleMenuClose()
		}
	}

	const handleManageUser = async (evt) => {
		navigate(selectedUser.user_id)
		evt.stopPropagation()
		evt.preventDefault()
		handleMenuClose()


	}

	const handleSearch = async () => {
		setSearchedTerm(searchTerm)
	}

	const clearSearch = async () => {
		setSearchTerm('')
		setSearchedTerm('')
	}

	const columns = [
		{ id: 'search_tokens', label: 'Name', align: 'left' },
		{ id: 'package_names', label: 'Packages', align: 'left' },
		{ id: 'created_at', label: 'Created', align: 'right' },
		{ id: 'email_confirmed_at', label: 'Email Confirmed', align: 'right' },
		{ id: 'last_sign_in_at', label: 'Last Sign In', align: 'right' }
	]

	return (
		<Container maxWidth="xl">
			<Grid xs={12} sx={{mb: 1}}>
				<LeftRight
					left={
						<Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
							<IconButton onClick={() => navigate(`/admin`)}>
								<ArrowBack />
							</IconButton>
							<Typography variant={"h5"}>Users</Typography>
						</Box>
					}
					right={
						<Box display='flex' alignItems='center' border='1px solid #ccc' borderRadius={2} px={1}>
							<InputBase
								placeholder='Search users'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleSearch()
								}}
								sx={{ ml: 1, flex: 1 }}
							/>
							{
								searchedTerm &&
								<IconButton onClick={clearSearch}>
									<Cancel color={"error"}/>
								</IconButton>
							}
							<IconButton onClick={handleSearch} disabled={!searchTerm}>
								{searchTerm ? <ArrowForward color={"primary"} /> : <Search />}
							</IconButton>
						</Box>
					}
				/>
			</Grid>
			<Grid xs={12}>
				<TableContainer>
					<Table sx={{ border: "1px solid #ccc" }} aria-label="simple table">
						<TableHead>
							<TableRow>
								{columns.map(col => (
									<TableCell key={col.id} align={col.align}>
										<TableSortLabel
											active={sortBy === col.id}
											direction={sortBy === col.id ? sortDir : 'asc'}
											onClick={() => {
												if (sortBy === col.id) {
													setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
												} else {
													setSortBy(col.id)
													setSortDir('asc')
												}
											}}
										>
											{col.label}
										</TableSortLabel>
									</TableCell>
								))}
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{users.map((row) => (
								<TableRow key={row.email} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
									<TableCell>
										<Typography>{row.first_name} {row.last_name}</Typography>
										<Typography variant={"body2"} sx={{color: theme.palette.text.secondary}}>
											<Link href={`mailto:${row.email}`}>{row.email}</Link>

										</Typography>
									</TableCell>
									<TableCell>
										{row.package_names}<br/>{row.package_options}
									</TableCell>
									<TableCell align="right">
										{row.created_at ? moment(row.created_at).calendar() : ""}
									</TableCell>
									<TableCell align="right">
										{row.email_confirmed_at ? moment(row.email_confirmed_at).calendar() : "N/A"}
									</TableCell>
									<TableCell align="right">
										{row.last_sign_in_at ? moment(row.last_sign_in_at).calendar() : "N/A"}
									</TableCell>
									<TableCell align="right">
										<IconButton onClick={(e) => handleMenuOpen(e, row)}>
											<MoreVert />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>
			<Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={4} >
				<Button variant="outlined" disabled={page === 1} onClick={handlePrevPage}>
					Previous
				</Button>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography variant="body2">
						Page {page} of {totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1}
					</Typography>
					<Typography variant="body2">Page Size:</Typography>
					<Select
						value={pageSize}
						onChange={handlePageSizeChange}
						size="small"
					>
						<MenuItem value={50}>50</MenuItem>
						<MenuItem value={250}>250</MenuItem>
						<MenuItem value={500}>500</MenuItem>
						<MenuItem value={1000}>1000</MenuItem>

					</Select>
				</Box>
				<Button
					variant="outlined"
					disabled={page * pageSize >= totalCount || totalCount === 0}
					onClick={handleNextPage}
				>
					Next
				</Button>
			</Box>

			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleMenuClose}
			>
				<MenuItem onClick={handleManageUser}>
					<Edit sx={{ marginRight: 1 }} />
					Manage User
				</MenuItem>
				<Divider/>
				<MenuItem onClick={handleImpersonateUser}>
					<SwitchAccount sx={{ marginRight: 1 }} />
					Impersonate User
				</MenuItem>
				<Divider/>
				<MenuItem onClick={handleDeleteUser}>
					<Delete sx={{ marginRight: 1 }} />
					Delete User
				</MenuItem>
				<Divider/>
				<MenuItem onClick={()=>setShowForceDelete(true)}>
					<DeleteForever sx={{ marginRight: 1 }} />
					Force Delete User
				</MenuItem>
			</Menu>
			<Dialog open={showForceDelete} onClose={handleCloseForceDelete}>
				<DialogTitle>Confirm Deletion</DialogTitle>
				<DialogContent>
					<Typography gutterBottom>
						All user data for
						<Link href={"javascript:void(0);"}
						      sx={{mx: 1, fontWeight: "bold"}}
						      onClick={() => setTypeDelete(selectedUser?.email) }
						>
							{selectedUser?.email}
						</Link>
						will be deleted, including PrepKits, Messages, Answer History, and Licenses.
					</Typography>
					<Alert severity={"error"} sx={{my: 2}}>
						<strong>THIS OPERATION IS <u>PERMANENT</u> AND CANNOT BE UNDONE.</strong>
					</Alert>
					<TextField
						label={`Type ${selectedUser?.email} to confirm`}
						variant="outlined"
						fullWidth
						value={typeDelete}
						onChange={(e) => setTypeDelete(e.target.value)}
						margin="dense"
						error={!!typeDelete && !isDeleteConfirmed}
						helperText={!!typeDelete && !isDeleteConfirmed ? `You must type ${selectedUser?.email} exactly to proceed.` : ''}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseForceDelete} color="primary">
						Cancel
					</Button>
					<Button
						onClick={handleForceDeleteUser}
						color="error"
						disabled={!isDeleteConfirmed}
						variant="contained"
					>
						Confirm Force Delete
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={!!error}
				onClose={()=>setError(null)}
				autoHideDuration={4000}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={()=>setError(null)} severity="error" variant="filled">
					{error}
				</Alert>
			</Snackbar>
		</Container>
	)
}

export default Users
